import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { useIntentsPaged, useInteractionsForIntent, useSetIntentReviewStatus } from "./data.ts";
import { MetricTiles, ThreatPill, ReviewPill, statusLevel } from "./components.tsx";
import { DetailDrawer } from "./DetailDrawer.tsx";
import { InteractionDetail } from "./InteractionDetail.tsx";
import type { Interaction, Intent, IntentReviewStatus } from "./types.ts";
import "./agentdna.css";

const REVIEW_STATUSES: readonly IntentReviewStatus[] = ["Ongoing", "Acknowledged", "Flagged"];

/**
 * Intent detail page (`/intents/:intentId`), modelled on the AgentDNA dashboard: a hero row with the
 * owner, intent id, started time, status and threat count, plus View Flow and Change Status actions;
 * activity metric tiles; and the table of this intent's interactions (row → interaction drawer).
 * Change Status is an audited operator write (`POST /intents/:id/status`). Viewer can read; the
 * status menu is shown only to operator+ (the server enforces regardless).
 */
export function IntentDetailPage(): ReactNode {
  const { role } = useIdentity();
  const navigate = useNavigate();
  const { intentId = "" } = useParams();
  const { data: intents, loading: intentsLoading } = useIntentsPaged(1);
  const { data: interactions, loading: ixnLoading } = useInteractionsForIntent(intentId);
  const { setStatus, saving, error: statusError } = useSetIntentReviewStatus();
  const [selected, setSelected] = useState<Interaction | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [intentOverride, setIntentOverride] = useState<Intent | null>(null);

  const base = intents.items.find((x) => x.id === intentId) ?? null;
  const intent = intentOverride ?? base;
  const canWrite = roleAtLeast(role, "operator");
  const state = resolveState(role, intentsLoading || ixnLoading);
  const titleId = "interaction-detail-title";

  const threatCount = interactions.items.filter((i) => i.threat).length;

  const onChangeStatus = async (next: IntentReviewStatus): Promise<void> => {
    setMenuOpen(false);
    if (intent === null || next === intent.reviewStatus) return;
    try {
      const updated = await setStatus(intent.id, next);
      setIntentOverride(updated);
    } catch {
      // The hook surfaces `statusError`; nothing else to do here.
    }
  };

  const columns: readonly Column<Interaction>[] = [
    { key: "initiator", header: "Initiator", isRowHeader: true, cell: (r) => r.initiator.name },
    { key: "target", header: "Target", cell: (r) => r.target.name },
    { key: "runtime", header: "Runtime", align: "right", cell: (r) => `${r.runtime} ms` },
    { key: "threat", header: "Threat", cell: (r) => <ThreatPill threat={r.threat} /> },
    {
      key: "created",
      header: "Time",
      align: "right",
      cell: (r) => <time dateTime={isoAttr(r.created)}>{formatDateTime(r.created)}</time>,
    },
  ];

  return (
    <ScreenScaffold
      title={intent?.name ?? "Intent"}
      subtitle="Every interaction that belongs to this intent."
      state={state}
    >
      <p className="adna-breadcrumb">
        <Link className="drawer-link" to="/intents">
          ← All intents
        </Link>
      </p>

      {intent !== null && (
        <div className="adna-hero">
          <div className="adna-hero__facts">
            <div className="adna-hero__fact">
              <span className="adna-hero__label">Owner</span>
              <span className="adna-hero__value">{intent.initiator.name}</span>
            </div>
            <div className="adna-hero__fact">
              <span className="adna-hero__label">Intent ID</span>
              <span className="adna-hero__value adna-mono">{intent.id}</span>
            </div>
            <div className="adna-hero__fact">
              <span className="adna-hero__label">Started</span>
              <span className="adna-hero__value">
                <time dateTime={isoAttr(intent.started)}>{formatDateTime(intent.started)}</time>
              </span>
            </div>
            <div className="adna-hero__fact">
              <span className="adna-hero__label">Status</span>
              <span className="adna-hero__value">
                <ReviewPill status={intent.reviewStatus} />
              </span>
            </div>
            <div className="adna-hero__fact">
              <span className="adna-hero__label">Threats detected</span>
              <span className={`adna-hero__value ${threatCount > 0 ? "drawer-threat" : ""}`}>
                {threatCount}
              </span>
            </div>
          </div>
          <div className="adna-hero__actions">
            <button
              type="button"
              className="adna-btn adna-btn--primary"
              onClick={() => navigate(`/intents/${encodeURIComponent(intent.id)}/flow`)}
            >
              View Flow
            </button>
            {canWrite && (
              <div className="adna-menu">
                <button
                  type="button"
                  className="adna-btn"
                  onClick={() => setMenuOpen((o) => !o)}
                  disabled={saving}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  {saving ? "Saving…" : "Change status"}
                </button>
                {menuOpen && (
                  <div className="adna-menu__list" role="menu">
                    {REVIEW_STATUSES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        role="menuitemradio"
                        aria-checked={s === intent.reviewStatus}
                        className="adna-menu__item"
                        onClick={() => void onChangeStatus(s)}
                        disabled={saving}
                      >
                        {s}
                        {s === intent.reviewStatus && <span aria-hidden="true"> ✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {statusError !== null && (
        <p className="adna-error" role="alert">
          Could not change status: {statusError.message}
        </p>
      )}

      {intent !== null && (
        <MetricTiles
          tiles={[
            { label: "Interactions", value: intent.interactionsCount },
            { label: "Agents touched", value: intent.agentsInteracted },
            { label: "Apps touched", value: intent.toolsInteracted },
            { label: "Threats", value: threatCount },
            { label: "Reliability", value: intent.score },
          ]}
        />
      )}

      {intent !== null && (
        <div className="adna-hero__status-row">
          <StatusBadge level={statusLevel(intent.status)} size="sm" />
          {intent.provenanceRecordID !== "" && (
            <span className="adna-mono adna-hero__prov">Provenance: {intent.provenanceRecordID}</span>
          )}
        </div>
      )}

      <DenseTable
        caption="Interactions for this intent"
        columns={columns}
        rows={interactions.items}
        rowKey={(r) => r.id}
        onRowClick={(r) => setSelected(r)}
        rowLabel={(r) => `View details for the interaction from ${r.initiator.name} to ${r.target.name}`}
        emptyMessage="No interactions recorded for this intent yet."
      />

      <DetailDrawer open={selected !== null} onClose={() => setSelected(null)} labelledBy={titleId}>
        {selected !== null && (
          <InteractionDetail interaction={selected} onClose={() => setSelected(null)} titleId={titleId} />
        )}
      </DetailDrawer>
    </ScreenScaffold>
  );
}

function resolveState(role: ReturnType<typeof useIdentity>["role"], loading: boolean): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet. The server enforces access regardless."
          : "Intents need at least the viewer role.",
    };
  }
  if (loading) return { kind: "loading" };
  return { kind: "ready" };
}
